import { css } from "@emotion/css";
import { AlignRight } from "lucide-react";

export const primaryBlue = "#0078FF"; // 主蓝色
export const lightBlue = "#E6F0FF"; // 浅蓝色气泡背景
export const darkGray = "#333"; // 深灰色文本
export const lightGray = "#888"; // 浅灰文本
export const borderGray = "#ddd"; // 边框灰色

// 辅助函数：判断是否为数字，如果是数字则添加 px 单位
const addPxIfNumber = (value: number | string): string => {
  return typeof value === "number" ? `${value}px` : value;
};

// 基础样式集合
export const basicStyles = {
  w: (width: number | string) => css`
    width: ${addPxIfNumber(width)};
  `,
  h: (height: number | string) => css`
    height: ${addPxIfNumber(height)};
  `,
  p: (padding: string) => css`
    padding: ${padding};
  `,
  pb: (padding: number | string) => css`
    padding-bottom: ${addPxIfNumber(padding)};
  `,
  pt: (padding: number | string) => css`
    padding-top: ${addPxIfNumber(padding)};
  `,
  pl: (padding: number | string) => css`
    padding-left: ${addPxIfNumber(padding)};
  `,
  pr: (padding: number | string) => css`
    padding-right: ${addPxIfNumber(padding)};
  `,
  m: (margin: string) => css`
    margin: ${margin};
  `,
  mb: (margin: number | string) => css`
    margin-bottom: ${addPxIfNumber(margin)};
  `,
  mt: (margin: number | string) => css`
    margin-top: ${addPxIfNumber(margin)};
  `,
  ml: (margin: number | string) => css`
    margin-left: ${addPxIfNumber(margin)};
  `,
  mr: (margin: number | string) => css`
    margin-right: ${addPxIfNumber(margin)};
  `,
  text: (size: number | string) => css`
    font-size: ${addPxIfNumber(size)};
  `,
  weight: (size: number) => css`
    font-weight: ${size};
  `,
  textColor: (c: string) => css`
    color: ${c};
  `,
  bg: (c: string) => css`
    background-color: ${c};
  `,
  rs: (radius: number | string) => css`
    border-radius: ${addPxIfNumber(radius)};
  `,
  gap: (g: number | string) => css`
    gap: ${addPxIfNumber(g)};
  `,
  flex: css`
    display: flex;
    align-items: center;
  `,
  flexCenter: css`
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  flexBetween: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  flexColumn: css`
    display: flex;
    flex-direction: column;
  `,
  flex1: css`
    flex: 1;
  `,
  cursor: (type: string) => css`
    cursor: ${type};
  `,
  border: (color: string, width = "1px") => css`
    border: ${width} solid ${color};
  `,
  boxShadow: (shadow: string) => css`
    box-shadow: ${shadow};
  `,
  transition: (property = "all", duration = "0.2s") => css`
    transition: ${property} ${duration} ease;
  `,
  overflow: (type: string) => css`
    overflow: ${type};
  `,
  position: (type: string) => css`
    position: ${type};
  `,
  zIndex: (index: number) => css`
    z-index: ${index};
  `,

  flexCenterGap2: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,

  flexCenterGap4: css`
    display: flex;
    align-items: center;
    gap: 4px;
    color: #7f56d9;
    cursor: pointer;
    &:hover {
      opacity: 0.8;
    }
  `,
};

// 高阶函数：自动合并基础样式
export function withBasicStyles<T extends Record<string, any>>(
  useCustomStyles: () => T,
) {
  return () => {
    const custom = useCustomStyles();
    const result = {
      ...basicStyles,
      ...custom, // 组件样式放在后面，优先覆盖基础样式
    };
    return result as T & typeof basicStyles;
  };
}
